import { createReducer, on, State } from '@ngrx/store';
import { CalculationState } from '../../shared/models/calculation-state';
import { CalculationActions } from './calculation-action-types';
import { InterestPeakInputDTO } from '../../openapi';

export const initialState: CalculationState = {
  calculation: null,
  loading: false,
  error: null,
};

export const calculationReducer = createReducer(
  initialState,
  on(CalculationActions.openCalculation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.loadCalculation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.loadCalculationSuccess, (state, { calculation }) => ({
    ...state,
    calculation,
    loading: false,
    error: null,
  })),
  on(CalculationActions.loadCalculationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(CalculationActions.resetCalculationState, (state) => ({
    ...state,
    loading: false,
    error: null,
    calculation: null,
  })),
  on(CalculationActions.clearStore, (state) => ({
    ...state,
    calculation: null,
  })),
  // calculation summary
  on(CalculationActions.saveCalculationSummary, (state, { id, summary, redirectUrl }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationSummarySuccess, (state, { summary }) => {
    const existingInputs = state.calculation?.interestPeak?.interestPeakInputs ?? [];
    const updatedInputs: InterestPeakInputDTO[] = (summary.clientPayments ?? []).map((payment) => {
      const existing = existingInputs.find((inp) => inp.id === payment.id);
      return existing ? { ...existing, clientPayment: payment.clientPayment, month: payment.month } : { ...payment };
    });
    return {
      ...state,
      calculation: {
        ...state.calculation!,
        ...summary.calculationSummaryForm,
        status: summary.status,
        openedById: summary.openedById,
        editMode: summary.editMode,
        interestPeak: {
          ...state.calculation?.interestPeak,
          interestPeakInputs: updatedInputs,
        },
      },
      loading: false,
      error: null,
    };
  }),
  on(CalculationActions.saveCalculationSummaryFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving calculation summary',
  })),
  // calculation settings
  on(CalculationActions.saveCalculationSettings, (state, { id, settings, redirectUrl }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationSettingsSuccess, (state, { calculation }) => ({
    ...state,
    calculation: {
      ...calculation,
    },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationSettingsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  // calculation escalations
  on(CalculationActions.saveCalculationEscalations, (state, { id, escalations, redirectUrl }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationEscalationsSuccess, (state, { calculation }) => ({
    ...state,
    calculation: {
      ...calculation,
    },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationEscalationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(CalculationActions.clearError, (state) => ({
    ...state,
    error: null,
  })),
  // calculation detailed
  on(CalculationActions.saveCalculationDetailed, (state, { id, details, redirectUrl }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationDetailedSuccess, (state, { calculation }) => ({
    ...state,
    calculation: {
      ...calculation,
    },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationDetailedFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving calculation detailed',
  })),
  // cost items (erection & startup)
  on(CalculationActions.saveCalculationCostItems, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationCostItemsSuccess, (state, { calculation }) => ({
    ...state,
    calculation: { ...calculation },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationCostItemsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving calculation cost items',
  })),
  // extras distribution
  on(CalculationActions.saveCalculationExtrasDistribution, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationExtrasDistributionSuccess, (state, { calculation }) => ({
    ...state,
    calculation: { ...calculation },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationExtrasDistributionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving calculation extras distribution',
  })),
  // total calculation / exe sum 1
  on(CalculationActions.saveCalculationCustomerPrice, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationCustomerPriceSuccess, (state, { calculation }) => ({
    ...state,
    calculation: { ...calculation },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationCustomerPriceFailure, (state) => ({
    ...state,
    loading: false,
    error: 'Error saving customer price',
  })),
  on(CalculationActions.saveCalculationTotal, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationTotalSuccess, (state, { calculation }) => ({
    ...state,
    calculation: { ...calculation },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationTotalFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving total calculation',
  })),
  // missing dpbs
  on(CalculationActions.saveCalculationMissingDpbs, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationMissingDpbsSuccess, (state, { calculation }) => ({
    ...state,
    calculation: { ...calculation },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationMissingDpbsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving missing DPBs',
  })),
  // calculation financial costs
  on(CalculationActions.saveCalculationFinancialCosts, (state, { id, costs, redirectUrl }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(CalculationActions.saveCalculationFinancialCostsSuccess, (state, { calculation }) => ({
    ...state,
    calculation: {
      ...calculation,
    },
    loading: false,
    error: null,
  })),
  on(CalculationActions.saveCalculationFinancialCostsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: 'Error saving calculation financial costs',
  }))
);
